import apiClient from './apiClient';

// ============================================================
// Types -- matching backend Go models (c209/models/c209.go)
// ============================================================

export interface FJ710 {
  fj710_01: string;         // Application No (PK)
  fj710_11: string | null;  // Application date
  fj710_12: string;         // Sample order No (FK to FC150)
  fj710_13: string;         // Department code
  fj710_14: string | null;  // Sample needed date
  fj710_151: number;        // Sample quantity
  fj710_152: string;        // Sample quantity unit
  fj710_161: string;        // Estimated sell price currency
  fj710_162: number;        // Estimated sell price
  fj710_163: string;        // Estimated sell price unit
  fj710_171: string;        // Estimated cost currency
  fj710_172: number;        // Estimated cost price
  fj710_173: string;        // Estimated cost unit
  fj710_181: number;        // Extra price 1
  fj710_182: string;        // Extra unit 1
  fj710_191: string;        // Extra currency 2
  fj710_192: number;        // Extra price 2
  fj710_201: number;        // Extra price 3
  fj710_202: string;        // Extra unit 3
  fj710_21: string;         // Processing type (memo)
  fj710_221: string;        // Processing requirement 1 (memo)
  fj710_222: string;        // Processing requirement 2 (memo)
  fj710_223: string;        // Processing requirement 3 (memo)
  fj710_224: string;        // Processing requirement 4 (memo)
  fj710_225: string;        // Processing requirement 5 (memo)
  fj710_231: string;        // Remark code
  fj710_232: string;        // Remark content (memo)
  fj710_24: string | null;  // Reserved date
  fj710_251: string;        // R&D reply code
  fj710_252: string;        // R&D reply content (memo)
  fj710_26: string;         // Product spec (memo)
  fj710_27: number;         // Development progress count
  fj710_28: string;         // Product name (memo)
  fj710_29: number;         // Color detail count
  fj710_30: string;         // Reserved field
  fj710_31: number;         // Reserved numeric
  fj710_32: string;         // Reserved text
  fj710_80: string;         // Approval status (N/P)
  fj710_81: string;         // Reserved status
  fj710_82: string | null;  // Approval date
  fj710_83: string;         // Approval user
  fj710_91: string;         // Modified by
  fj710_92: string | null;  // Modified at
  fj710_93: string;         // Created by

  // Associations
  progresses?: FJ711[];
  colors?: FJ712[];
}

export interface FJ711 {
  fj711_01: string;  // Application No (FK)
  fj711_02: number;  // Sequence no
  fj711_11: string;  // Progress code
  fj711_12: string;  // Progress detail (memo)
}

export interface FJ712 {
  fj712_01: string;         // Application No (FK)
  fj712_02: number;         // Sequence no
  fj712_11: string;         // Color code
  fj712_12: number;         // Quantity
  fj712_13: string;         // Print color match
  fj712_15: string;         // Chinese color name
  fj712_16: string;         // English color name
  fj712_91: string;         // Modified by
  fj712_92: string | null;  // Modified at
  fj712_93: string;         // Created by
}

// ============================================================
// Request / Response types
// ============================================================

export interface C209ListResponse {
  data: FJ710[];
  total: number;
  page: number;
  size: number;
}

export interface C209CreateRequest {
  master: Partial<FJ710>;
  progresses: Partial<FJ711>[];
  colors: Partial<FJ712>[];
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const c209Api = {
  async list(page = 1, size = 20): Promise<C209ListResponse> {
    const res = await apiClient.get('/api/c209', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FJ710> {
    const res = await apiClient.get(`/api/c209/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: C209CreateRequest): Promise<FJ710> {
    const res = await apiClient.post('/api/c209', data);
    return res.data;
  },

  async update(id: string, data: C209CreateRequest): Promise<FJ710> {
    const res = await apiClient.put(`/api/c209/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/c209/${encodeURIComponent(id)}`);
  },

  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/c209/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
